import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { business: "", industry: "", understanding: "", email: "", website: "" };

export default function RequestConcept() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business || !form.email) {
      toast.error("Please add your business name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/concept-requests`, form);
      toast.success("Request sent — we'll reply within 3 working days.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const label = "block text-sm text-[#78716C] mb-2";
  const input =
    "w-full bg-white border border-[#E8E4D9] px-5 py-4 text-base outline-none focus:border-[#C5A059] transition-colors placeholder:text-[#78716C]/50";

  return (
    <main className="bg-[#F3EFE6] min-h-screen" data-testid="request-concept-page">
      <Navbar />
      <section className="pt-36 md:pt-44 pb-28">
        <div className="max-w-[760px] mx-auto px-6 md:px-10">
          <button
            onClick={() => navigate(-1)}
            className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#78716C] mb-10"
          >
            <ArrowLeft size={15} className="group-hover:-translate-x-1 transition-transform" />
            Back
          </button>
          <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-5">— Request a Concept</p>
          <h1 className="font-serif font-light text-5xl md:text-7xl tracking-tight leading-[0.95]">
            Tell us about your establishment.
          </h1>
          <p className="text-base md:text-lg text-[#1A1713]/70 mt-6 max-w-xl leading-relaxed">
            We&rsquo;ll reply within 3 working days with a short concept
            direction — no cost, no obligation.
          </p>

          <Reveal>
            <form onSubmit={submit} className="mt-12 space-y-7" data-testid="concept-form">
              <div>
                <label className={label}>Business / establishment name</label>
                <input data-testid="cf-business" className={input} placeholder="e.g. Solace Retreats" value={form.business} onChange={set("business")} />
              </div>
              <div>
                <label className={label}>Industry</label>
                <input data-testid="cf-industry" className={input} placeholder="e.g. Hospitality, education, F&B" value={form.industry} onChange={set("industry")} />
              </div>
              <div>
                <label className={label}>What&rsquo;s the one thing you wish people understood about your brand?</label>
                <textarea data-testid="cf-understanding" rows={4} className={input + " resize-none"} placeholder="A sentence or two is enough" value={form.understanding} onChange={set("understanding")} />
              </div>
              <div>
                <label className={label}>Email</label>
                <input data-testid="cf-email" type="email" className={input} placeholder="you@company.com" value={form.email} onChange={set("email")} />
              </div>
              <div>
                <label className={label}>Instagram / website (optional)</label>
                <input data-testid="cf-website" className={input} placeholder="Helps us understand your current presence" value={form.website} onChange={set("website")} />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="cf-submit"
                className="bg-[#1A1713] text-[#FDFBF7] px-9 py-4 text-sm tracking-wide font-medium disabled:opacity-50 transition-transform active:scale-95 hover:bg-[#C5A059] hover:text-[#1A1713]"
              >
                {loading ? "Sending…" : "Send request"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
      <Footer />
    </main>
  );
}
